import { ChangeEvent, ChangeEventHandler, useCallback, useEffect, useState } from 'react';
import { WrappedFieldProps } from 'redux-form';
import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import FormError from 'inquiry/components/FormError';
import { containerStyles, labelSSTStyles } from 'inquiry/mixins/form';
import { AllowedFileUploadTypes, InputTextBaseStyles } from 'lib/interfaces/ui-components';

export const FILE_EXISTING_TAG = 'USE PREVIOUS FILE';

const FileUploadContainer = styled('div')<{
  styles?: InputTextBaseStyles;
}>`
  ${containerStyles}
  ${({ styles }) => labelSSTStyles(styles)}
`;

// TODO: [thwang] will probably need custom HTML so we can properly localize text.
const UploadInput = styled('input')`
  display: block;
`;

interface ThemedFormFileUploadProps {
  id: string;
  label?: string;
  placeholder?: string;
  fileUploadLimit: number;
  allowedFileTypes: AllowedFileUploadTypes[];
  styles?: InputTextBaseStyles;
  disabled?: boolean;
  prefill:
    | {
        filename: string;
        url: string;
      }[]
    | string[];
}

const ThemedFormFileUpload = (props: WrappedFieldProps & ThemedFormFileUploadProps) => {
  const {
    input: { onChange },
    meta: { error, submitFailed },
    id,
    label,
    fileUploadLimit,
    allowedFileTypes,
    // Other
    disabled,
    styles,
    prefill,
  } = props;
  const hasError = !!(error && submitFailed);
  const [newFileUploaded, setNewFileUploaded] = useState<boolean>(false);

  // NOTE: set input to an empty list on render, so that submitting an empty + optional field
  // will override the existing files submitted to the field
  useEffect(() => {
    const initialValue = prefill ? FILE_EXISTING_TAG : [];
    onChange(initialValue);
  }, []);

  const onFileChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (fileInput: ChangeEvent<HTMLInputElement>) => {
      const files = (fileInput.target as HTMLInputElement).files;
      // NOTE: fileInput.target.files is an object, need to map to array
      // https://developer.mozilla.org/en-US/docs/Web/API/FileList
      onChange(() => {
        const filesArray = Array.from(files ?? []);
        if (filesArray && filesArray.length) {
          setNewFileUploaded(true);
          return filesArray;
        }
      });
    },
    [onChange],
  );

  // TODO: [File Upload] maybe expand to allow drag + drop via useDropzone
  // replace generic upload "Choose file" with placeholder?
  // InputStyles.Material vs InputStyles.Outlined

  return (
    <FileUploadContainer styles={styles}>
      {label && (
        <Markdown<'label'> wrapper='label' wrapperProps={{ htmlFor: id }}>
          {label}
        </Markdown>
      )}
      <UploadInput
        id={id}
        name={id}
        accept={allowedFileTypes.join(',')}
        data-test={`${id}__upload-input`}
        disabled={disabled}
        multiple={fileUploadLimit > 1}
        onChange={onFileChange}
        type='file'
        aria-errormessage={`${id}--error`}
        aria-describedby={`${id}--error`}
      />
      {prefill && !newFileUploaded && (
        <Markdown>
          {prefill
            .map((item) => {
              if (typeof item === 'string') {
                return `- ${item}`;
              } else {
                return `- [${item.filename}](${item.url})`;
              }
            })
            .join('\n')}
        </Markdown>
      )}
      <FormError id={`${id}--error`} hasError={hasError} errorMessage={error} styles={styles} />
    </FileUploadContainer>
  );
};

export default ThemedFormFileUpload;
